import { IRowNode } from 'ag-grid-community';
import { set } from 'lodash-es';
import {
  CaptableSource,
  CaptableType,
  ICaptableDataModel,
  IStakeholderInvestmentDataModel,
} from '../../../data-models/captable2.data-model';
import { ICompanyDataModel, isPortfolioCompany } from '../../../data-models/company.data-model';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import {
  CaptableChangeType,
  ClassificationProvenance,
  ICaptableVersion,
  ICaptableVersionGroup,
} from '../../../services/queries/MaggieCapTableQueries';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { ViewModelInvestmentType } from '../../../schemas/common-schema-defs';
import { FMT } from '../../../util/formatter-service';
import { getShareClassType, ShareClass } from '../../../view-models/captable.view-model';
import type { ICaptableSummaryRowData } from './SummaryGrid/CaptableSummaryRowData';
import { getNormalizedMetrics } from './SummaryGrid2/CaptableSummaryRowData2';
import type { ICaptableSummaryRowData2 } from './SummaryGrid2/CaptableSummaryRowData2';

export function isDuplicateSource(captable: ICaptableDataModel) {
  return captable.type === CaptableType.system && captable.source !== CaptableSource.none;
}

export function isUserCreated(captable: ICaptableDataModel) {
  return captable.type === CaptableType.user;
}

export function isClientInvestment(
  investment: Pick<IStakeholderInvestmentDataModel, 'fundId' | 'stakeholderName'>
) {
  return investment.fundId != null;
}

export function hasActiveHoldings(
  shares: number | null | undefined,
  ownershipPercentage: number | null | undefined
): boolean {
  return (shares ?? 0) > 0 || (ownershipPercentage ?? 0) > 0;
}

// SAFE/Note rows carry no shares/ownership, so gauge holdings by invested amount instead.
export function summaryRowHasHoldings(row: ICaptableSummaryRowData2): boolean {
  if (row.investmentType === ViewModelInvestmentType.notes) {
    return (row.amount ?? 0) > 0;
  }
  return hasActiveHoldings(row.sharesHeld, row.ownershipPercentage);
}

// Detailed grid aggregates per stakeholder; detect a SAFE/Note holding via its per-type breakdown.
export function hasActiveNoteHolding(row: ICaptableSummaryRowData): boolean {
  const notes = row.investments?.[ViewModelInvestmentType.notes];
  if (!notes) {
    return false;
  }
  return Object.values(notes).some((inv) => (inv.principal ?? 0) > 0 || (inv.cashRaised ?? 0) > 0);
}

export const CaptableSourceMap = {
  [CaptableSource.aumni]: 'Aumni',
  [CaptableSource.carta]: 'Carta',
  [CaptableSource.diligentEquity]: 'Diligent Equity',
  [CaptableSource.dealDocs]: 'Deal Docs',
  [CaptableSource.managedServices]: 'Managed Services',
  [CaptableSource.snowflake]: 'Snowflake',
  [CaptableSource.kp]: 'Snowflake',
  [CaptableSource.none]: '',
};

export function getCaptableSourceLabel(captable: Pick<ICaptableDataModel, 'source' | 'type'>) {
  // Managed-services data is ops-curated regardless of which row type carries it.
  if (captable.source === CaptableSource.managedServices) {
    return CaptableSourceMap[captable.source];
  }
  return captable.type === CaptableType.user ? 'User Created' : CaptableSourceMap[captable.source];
}

/**
 * Client-side selection/dedup key for a cap table or version group. Combines `type` with a
 * normalized source: user-created timelines collapse to `none` (matching {@link getCaptablePrimarySourceKey})
 * because a duplicated user cap table may retain its original `source` in one endpoint (`/captables`)
 * while the versions endpoint reports it differently — normalizing keeps both sides in agreement. The
 * `type` component still lets a duplicated user timeline coexist with the system one (FD-3733 BE note #3).
 */
export function getCaptableSourceKey(item: Pick<ICaptableDataModel, 'source' | 'type'>): string {
  const source = item.type === CaptableType.user ? CaptableSource.none : item.source;
  return `${source}:${item.type}`;
}

/**
 * Formats a version for the picklist. When a change classification exists, leads with its
 * `changeLabel` (FD-4108); otherwise falls back to `effectiveFrom` (+ "(current)" for the latest)
 * with `name` as an optional suffix (FD-3733 BE note #1). Do not rely on `name` alone — it is null
 * or a constant string for most sources.
 */
export function formatCaptableVersionOption(
  version: Pick<
    ICaptableVersion,
    'effectiveFrom' | 'isCurrent' | 'changeLabel' | 'needsReview' | 'classificationProvenance'
  >
): string {
  const date = FMT.format('date', version.effectiveFrom);
  const withCurrent = version.isCurrent ? `${date} (current)` : date;
  // Needs-review versions show only the date (the warning chip is the notice) — unless a human
  // classified the version; a manual label always shows even while absorbed snapshots stay flagged.
  const manuallyClassified = version.classificationProvenance === ClassificationProvenance.Manual;
  if (version.changeLabel && (!version.needsReview || manuallyClassified)) {
    return `${version.changeLabel} — ${withCurrent}`;
  }
  return withCurrent;
}

/** Human-readable labels for the change-type picker (FD-4108), mirroring the BE taxonomy. */
export const CaptableChangeTypeLabels: Record<CaptableChangeType, string> = {
  [CaptableChangeType.Genesis]: 'Initial Cap Table',
  [CaptableChangeType.PricedRound]: 'Priced Round',
  [CaptableChangeType.ConvertibleFinancing]: 'Convertible Financing',
  [CaptableChangeType.Conversion]: 'Conversion',
  [CaptableChangeType.SecondaryTransfer]: 'Secondary Transfer',
  [CaptableChangeType.OptionPoolAdjustment]: 'Option Pool Adjustment',
  [CaptableChangeType.ValuationUpdate]: 'Valuation Update',
  [CaptableChangeType.Recapitalization]: 'Recapitalization',
  [CaptableChangeType.LiquidityExit]: 'Liquidity / Exit',
  [CaptableChangeType.RestatementCorrection]: 'Restatement / Correction',
  [CaptableChangeType.NoMaterialChange]: 'No Material Change',
  [CaptableChangeType.NeedsReview]: 'Needs Review',
};

/** Change-type options for the classification edit picker, in the label map's order. */
export const captableChangeTypeOptions: ISimpleChoice<CaptableChangeType>[] = (
  Object.keys(CaptableChangeTypeLabels) as CaptableChangeType[]
).map((value) => ({ value, displayName: CaptableChangeTypeLabels[value] }));

/** Maps a captable to the source key used by the historical endpoint.
 * User-created captables always map to 'none', regardless of their `source` field
 * (which may reflect the original source if duplicated). */
export function getCaptablePrimarySourceKey(captable: ICaptableDataModel): CaptableSource {
  return isUserCreated(captable) ? CaptableSource.none : captable.source;
}

/**
 * Resolves which captable to select after a fetch, preserving the user's previous
 * selection by source key when possible.
 * Fallback order: match by source key → primary → first available.
 */
export function resolveSelectedCaptable(
  captables: ICaptableDataModel[],
  previousSelected: ICaptableDataModel | null | undefined
): ICaptableDataModel | null {
  const previousSourceKey = previousSelected ? getCaptablePrimarySourceKey(previousSelected) : null;
  const matchBySource =
    previousSourceKey != null
      ? captables.find((c) => getCaptablePrimarySourceKey(c) === previousSourceKey)
      : null;
  return matchBySource ?? captables.find((c) => c.primary) ?? captables[0] ?? null;
}

/**
 * Resolves which version to select from the version groups, preserving the user's previous selection
 * when possible. Fallback order within the resolved group: same version id → the current version →
 * first. Group fallback order: previous source key → primary group → first group.
 */
export function resolveSelectedVersion(
  groups: ICaptableVersionGroup[],
  previousSelected: Pick<ICaptableDataModel, 'id' | 'source' | 'type'> | null | undefined
): ICaptableVersion | null {
  if (!groups.length) {
    return null;
  }
  const previousSourceKey = previousSelected ? getCaptableSourceKey(previousSelected) : null;
  const group =
    (previousSourceKey != null
      ? groups.find((g) => getCaptableSourceKey(g) === previousSourceKey)
      : undefined) ??
    groups.find((g) => g.versions.some((v) => v.isPrimary)) ??
    groups[0];
  const previousVersion =
    previousSelected != null ? group.versions.find((v) => v.id === previousSelected.id) : undefined;
  return previousVersion ?? group.versions.find((v) => v.isCurrent) ?? group.versions[0] ?? null;
}

interface IInitialRowGetterParams {
  company: ICompanyDataModel;
  companyFundIds: Iterable<number>;
  shareClasses: ShareClass[];
  funds: IFundDataModel[];
}
export function getInitialClientInvestmentRows({
  company,
  funds,
  companyFundIds,
  shareClasses,
}: IInitialRowGetterParams) {
  function setShareClassData(row: Record<string, unknown>) {
    shareClasses.forEach((shareClass) => {
      set(row, shareClass.name, null);
    });
  }

  const rows = [];
  if (isPortfolioCompany(company!)) {
    for (const fundId of companyFundIds) {
      const row = { fundId };
      setShareClassData(row);
      rows.push(row);
    }
  } else {
    const row = { fundId: funds.at(0)!.id };
    setShareClassData(row);
    rows.push(row);
  }
  return rows;
}

/**
 * Checks if a row node is a data row (not a footer/total row)
 * Footer rows are calculated by ag-Grid for aggregations and should be excluded from data operations
 */
export function isDataRow(node: IRowNode): boolean {
  return !node.footer;
}

/**
 * Summary totals calculated from a captable.
 * These values are aggregated from individual stakeholder investments.
 */
export interface ICaptableSummaryTotals {
  /** Ownership % for client investments: (clientSharesHeld / totalSharesHeld) * 100 */
  clientOwnershipPercentage: number;
  /** Sum of normalized invested amount across all investments */
  totalAmount: number;
  /** Sum of normalized fully diluted shares across all investments */
  totalFullyDilutedShares: number;
  /** Total liquidation preference across all preferred classes (from API, not recomputed) */
  totalLiquidationPreference: number | null;
}

/**
 * Calculates summary totals from a captable by aggregating normalized metrics
 * from all stakeholder investments.
 *
 * Uses getNormalizedMetrics to map source fields based on investment type:
 * - amount: cashRaised (preferred/common/warrants/cash/debt) or principal (notes)
 * - sharesHeld: fullyDilutedShares (preferred/common/warrants) or options derivatives sum
 *
 * Client ownership percentage is calculated as (clientSharesHeld / totalSharesHeld) * 100,
 * matching the captableOwnershipAggFunc calculation in the grid.
 */
export function getCaptableSummaryTotals(captable: ICaptableDataModel | null): ICaptableSummaryTotals {
  const totalLiquidationPreference =
    captable?.investmentsSummaries?.preferredInvestmentsSummary?.totalLiquidationPreference ?? null;

  if (!captable?.captableInvestments?.length) {
    return {
      clientOwnershipPercentage: 0,
      totalAmount: 0,
      totalFullyDilutedShares: 0,
      totalLiquidationPreference,
    };
  }

  let clientSharesHeld = 0;
  let totalAmount = 0;
  let totalFullyDilutedShares = 0;

  captable.captableInvestments.forEach((investment) => {
    const investmentType = getShareClassType(investment.investmentType, investment.shareClassType);
    if (!investmentType) {
      return;
    }

    investment.stakeholderInvestments?.forEach((stakeholder) => {
      const metrics = getNormalizedMetrics(stakeholder, investmentType);

      totalAmount += metrics.amount ?? 0;
      totalFullyDilutedShares += metrics.sharesHeld ?? 0;

      if (isClientInvestment(stakeholder)) {
        clientSharesHeld += metrics.sharesHeld ?? 0;
      }
    });
  });

  const clientOwnershipPercentage = totalFullyDilutedShares
    ? (clientSharesHeld / totalFullyDilutedShares) * 100
    : 0;

  return { clientOwnershipPercentage, totalAmount, totalFullyDilutedShares, totalLiquidationPreference };
}
